import {install} from './install';
import {MessageFormatter} from './MessageFormatter';
import {convertToDateIfString, isObject} from './util';

const LIKELY_A_BASIC_FORMAT_RE = /\{\d+\}/;

function likelyUsesABasicFormat(message) {
    return !(message.indexOf('{') !== -1) || LIKELY_A_BASIC_FORMAT_RE.test(message);
}

/**
 * Copied/pasted/adapted from https://github.com/kazupon/vue-i18n
 */
export default class I18nPlugin {
    constructor(options = {}) {
        const locale = options.locale || document.getElementsByTagName('html')[0].getAttribute('lang') || 'en';
        const fallbackLocale = options.fallbackLocale || 'en';
        const messages =
            (options.messages && {[locale]: options.messages}) ||
            (options.messagesForLocale && {[locale]: options.messagesForLocale}) ||
            (window.HopMessages && {[locale]: window.HopMessages}) ||
            {};
        const dateTimeFormats = options.dateTimeFormats || {};
        const numberFormats = options.numberFormats || {};

        this._formatter = new MessageFormatter({
            logger: console,
        });
        this._root = options.root || null;
        this._fallbackRoot = options.fallbackRoot === undefined ? true : !!options.fallbackRoot;
        this._dateTimeFormatters = {};
        this._numberFormatters = {};

        this._translationActivated = sessionStorage.getItem('showTranslationsKeys') === 'true';
        // We set a body vue class to distinguish legacy and vue apps, in order to handle inline translation specifically for Vue apps
        // See lib-webcommon/webmvc-common/src/main/js/Hop/components/admin/translation-inline/translation-inline.js
        document.querySelector('body').classList.add('vue-app');

        this._locale = locale;
        this._fallbackLocale = fallbackLocale;
        this._messages = messages;
        this._dateTimeFormats = dateTimeFormats;
        this._numberFormats = numberFormats;
    }

    get locale() {
        return this._locale;
    }

    set locale(locale) {
        this._locale = locale;
    }

    get fallbackLocale() {
        return this._fallbackLocale;
    }

    set fallbackLocale(locale) {
        this._fallbackLocale = locale;
    }

    _getMessages() {
        return this._messages;
    }

    _getDateTimeFormats() {
        return this._dateTimeFormats;
    }

    _getNumberFormats() {
        return this._numberFormats;
    }

    orDefault(locale, key, result) {
        if (result != null) {
            return result;
        }
        return key;
    }

    _isFallbackRoot(val) {
        return !val && this._root != null && this._fallbackRoot;
    }

    //
    // Marker function used to used to require the inclusion of an i18n message.
    // Calls are detected, handled, and removed during a static code analysis
    // (see message-keys-extraction-plugin.js).
    //
    requireMsg() {
        // void

        // Allows for writing things such as: $requireMsg(`some.pattern.*`) + $msg(`some.pattern.${key}`)
        // for when the pattern can't be guessed correctly from the code.
        return '';
    }

    _formatIcuMessage(locale, key, message, args) {
        const params = args[0] || {};
        try {
            return this._formatter.formatIcuMessage(message, params, locale);
        } catch (e) {
            console.error(`Could not process message ${key} for locale ${locale} : ${message}`, e);
            return key;
        }
    }

    _icu(key, locale, messages, ...values) {
        if (!key) {
            return '';
        }

        if (this._translationActivated) {
            return '[icu] ' + key;
        }

        const _interpolate = (locale, messagesForLocale, key, args) => {
            if (!messagesForLocale) {
                return null;
            }

            const message = messagesForLocale[key];
            if (!message) {
                return null;
            }

            return this._formatIcuMessage(locale, key, message, args);
        };

        const ret = (() => {
            let res = _interpolate(locale, messages[locale], key, values);
            if (res != null) {
                return res;
            }

            res = _interpolate(this.fallbackLocale, messages[this.fallbackLocale], key, values);
            if (res != null) {
                return res;
            }

            return key;
        })();

        if (this._isFallbackRoot(ret)) {
            if (!this._root) {
                throw Error('unexpected error');
            }
            return this._root.icu(key, ...values);
        } else {
            return this.orDefault(locale, key, ret);
        }
    }

    _msg(key, locale, messages, ...values) {
        if (!key) {
            return '';
        }

        if (this._translationActivated) {
            return key;
        }

        const _interpolate = (locale, messagesForLocale, key, args) => {
            if (!messagesForLocale) {
                return null;
            }

            const message = messagesForLocale[key];
            if (!message) {
                return null;
            }

            if (likelyUsesABasicFormat(message)) {
                return this._formatter.formatBasicMessage(message, args);
            }
            return this._formatIcuMessage(locale, key, message, args);
        };

        const ret = (() => {
            let res = _interpolate(locale, messages[locale], key, values);
            if (res != null) {
                return res;
            }

            res = _interpolate(this.fallbackLocale, messages[this.fallbackLocale], key, values);
            if (res != null) {
                return res;
            }

            return key;
        })();

        if (this._isFallbackRoot(ret)) {
            if (!this._root) {
                throw Error('unexpected error');
            }
            return this._root.msg(key, ...values);
        } else {
            return this.orDefault(locale, key, ret);
        }
    }

    icu(key, ...values) {
        return this._icu(key, this.locale, this._getMessages(), ...values);
    }

    msg(key, ...values) {
        return this._msg(key, this.locale, this._getMessages(), ...values);
    }

    _localizeDateTime(value, locale, fallback, dateTimeFormats, key) {
        let _locale = locale;
        let formats = dateTimeFormats[_locale];

        // fallback locale
        if (formats == null || formats[key] == null) {
            _locale = fallback;
            formats = dateTimeFormats[_locale];
        }

        if (formats == null || formats[key] == null) {
            return null;
        } else {
            const format = formats[key];
            const id = `${_locale}__${key}`;
            let formatter = this._dateTimeFormatters[id];
            if (!formatter) {
                formatter = this._dateTimeFormatters[id] = new Intl.DateTimeFormat(_locale, format);
            }
            return formatter.format(value);
        }
    }

    _d(value, locale, key) {
        if (!value) {
            return '';
        }

        value = convertToDateIfString(value);

        if (!key) {
            return new Intl.DateTimeFormat(locale).format(value);
        }

        const ret = this._localizeDateTime(value, locale, this.fallbackLocale, this._getDateTimeFormats(), key);
        if (this._isFallbackRoot(ret)) {
            /* istanbul ignore if */
            if (!this._root) {
                throw Error('unexpected error');
            }
            return this._root.d(value, key, locale);
        } else {
            return ret || '';
        }
    }

    d(value, ...args) {
        let locale = this.locale;
        let key = null;

        if (args.length === 1) {
            if (typeof args[0] === 'string') {
                key = args[0];
            } else if (isObject(args[0])) {
                if (args[0].locale) {
                    locale = args[0].locale;
                }
                if (args[0].key) {
                    key = args[0].key;
                }
            }
        } else if (args.length === 2) {
            if (typeof args[0] === 'string') {
                key = args[0];
            }
            if (typeof args[1] === 'string') {
                locale = args[1];
            }
        }

        return this._d(value, locale, key);
    }

    _dt(value, locale, key) {
        if (!value) {
            return '';
        }

        value = convertToDateIfString(value);

        if (!key) {
            const options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            };
            return new Intl.DateTimeFormat(locale, options).format(value);
        }

        const ret = this._localizeDateTime(value, locale, this.fallbackLocale, this._getDateTimeFormats(), key);
        if (this._isFallbackRoot(ret)) {
            /* istanbul ignore if */
            if (!this._root) {
                throw Error('unexpected error');
            }
            return this._root.dt(value, key, locale);
        } else {
            return ret || '';
        }
    }

    dt(value, ...args) {
        let locale = this.locale;
        let key = null;

        if (args.length === 1) {
            if (typeof args[0] === 'string') {
                key = args[0];
            } else if (isObject(args[0])) {
                if (args[0].locale) {
                    locale = args[0].locale;
                }
                if (args[0].key) {
                    key = args[0].key;
                }
            }
        } else if (args.length === 2) {
            if (typeof args[0] === 'string') {
                key = args[0];
            }
            if (typeof args[1] === 'string') {
                locale = args[1];
            }
        }

        return this._dt(value, locale, key);
    }

    _localizeNumber(value, locale, fallback, numberFormats, key) {
        let _locale = locale;
        let formats = numberFormats[_locale];

        // fallback locale
        if (formats == null || formats[key] == null) {
            _locale = fallback;
            formats = numberFormats[_locale];
        }

        if (formats == null || formats[key] == null) {
            return null;
        } else {
            const format = formats[key];
            const id = `${_locale}__${key}`;
            let formatter = this._numberFormatters[id];
            if (!formatter) {
                formatter = this._numberFormatters[id] = new Intl.NumberFormat(_locale, format);
            }
            return formatter.format(value);
        }
    }

    _n(value, locale, key) {
        if (typeof value !== 'number') {
            return '';
        }
        if (!key) {
            return new Intl.NumberFormat(locale).format(value);
        }

        const ret = this._localizeNumber(value, locale, this.fallbackLocale, this._getNumberFormats(), key);
        if (this._isFallbackRoot(ret)) {
            /* istanbul ignore if */
            if (!this._root) {
                throw Error('unexpected error');
            }
            return this._root.n(value, key, locale);
        } else {
            return ret || '';
        }
    }

    n(value, ...args) {
        let locale = this.locale;
        let key = null;

        if (args.length === 1) {
            if (typeof args[0] === 'string') {
                key = args[0];
            } else if (isObject(args[0])) {
                if (args[0].locale) {
                    locale = args[0].locale;
                }
                if (args[0].key) {
                    key = args[0].key;
                }
            }
        } else if (args.length === 2) {
            if (typeof args[0] === 'string') {
                key = args[0];
            }
            if (typeof args[1] === 'string') {
                locale = args[1];
            }
        }

        return this._n(value, locale, key);
    }
}

I18nPlugin.install = install;
